import type { GsCheck, GsDiscrepancy } from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { linkKindLabel, statusLabel, statusTone } from '../helpers';
import { Err, Muted, ObjName, Row, TableTitle } from '../styles';
import { MarginSummary } from './MarginSummary';
import { DiscrepanciesTable } from './DiscrepanciesTable';

export interface GsCheckDetailData {
  check: GsCheck;
  discrepancies: GsDiscrepancy[];
}

export function CheckDetailCard({
  detail,
  canAnalyze,
  analyzing,
  can_write,
  exporting,
  sendingRequest,
  isAuthor,
  requestNotice,
  exportError,
  onAnalyze,
  onExport,
  onSendDsRequest,
  onDelete,
}: {
  detail: GsCheckDetailData;
  canAnalyze: boolean;
  analyzing: boolean;
  can_write: boolean;
  exporting: boolean;
  sendingRequest: boolean;
  isAuthor: boolean;
  requestNotice: string | null;
  exportError: string | null;
  onAnalyze: (id: string) => void;
  onExport: (id: string) => void;
  onSendDsRequest: () => void;
  onDelete: (id: string) => void;
}) {
  return (
    <Card>
      <Row>
        <div>
          <ObjName>{detail.check.object_name}</ObjName>
          <Muted>
            {linkKindLabel(detail.check.link_kind)}
            {detail.check.contract_ref ? ` · ${detail.check.contract_ref}` : ''} · позиций ГП{' '}
            {detail.check.gp_position_count} / СП {detail.check.sp_position_count} · находок:{' '}
            {detail.discrepancies.length}
          </Muted>
          {(detail.check.gp_party || detail.check.sp_party) && (
            <Muted>
              {detail.check.gp_party ?? '—'} → {detail.check.sp_party ?? '—'}
            </Muted>
          )}
        </div>
        <Row>
          <Badge $tone={statusTone(detail.check.status)}>
            {statusLabel(detail.check.status)}
          </Badge>
          {canAnalyze && (
            <Button
              $variant="primary"
              $size="sm"
              type="button"
              onClick={() => onAnalyze(detail.check.id)}
              disabled={analyzing || !can_write}
              title={!can_write ? 'Только чтение' : undefined}
            >
              {analyzing ? 'Запуск…' : 'Сверить'}
            </Button>
          )}
          {detail.check.status === 'analyzed' && (
            <Button
              $variant="secondary"
              $size="sm"
              type="button"
              onClick={() => onExport(detail.check.id)}
              disabled={exporting}
            >
              {exporting ? 'Готовлю…' : 'Отчёт (XLSX)'}
            </Button>
          )}
          {detail.check.status === 'analyzed' && detail.discrepancies.length > 0 && (
            <Button
              $variant="secondary"
              $size="sm"
              type="button"
              disabled={sendingRequest || !can_write}
              title="Инициировать дополнительное соглашение в Статут / Экономиста"
              onClick={onSendDsRequest}
            >
              {sendingRequest ? 'Отправляю…' : 'Заявка на ДС (Юристу)'}
            </Button>
          )}
          <Button
            $variant="ghost"
            $size="sm"
            type="button"
            disabled={!can_write || !isAuthor}
            title={
              !can_write
                ? 'Только чтение'
                : !isAuthor
                  ? 'Удалить сверку может только её автор'
                  : undefined
            }
            onClick={() => onDelete(detail.check.id)}
          >
            Удалить
          </Button>
        </Row>
      </Row>

      {requestNotice && <Muted $mt={8}>{requestNotice}</Muted>}
      {detail.check.status === 'failed' && detail.check.error_message && (
        <Err $mt={8}>{detail.check.error_message}</Err>
      )}
      {exportError && <Err $mt={8}>{exportError}</Err>}

      {detail.check.status === 'analyzed' && <MarginSummary check={detail.check} />}

      <TableTitle>
        Расхождения <Badge $tone="neutral">{detail.discrepancies.length}</Badge>
      </TableTitle>
      <DiscrepanciesTable items={detail.discrepancies} checkId={detail.check.id} />
    </Card>
  );
}
